import {
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownTrigger,
    IconButton,
    Kebab,
} from "@bcc-ui/ui-components";
import React from "react";
import { useTranslation } from "react-i18next";

import ReportRecheck from "components/reports/AutopilotRecheck";
import SingleReportDeletion from "components/reports/SingleReportDeletion";
import { AUTH_LICENSE_CONSUME, AUTH_REPORT_DELETE, AUTH_REPORT_EDIT } from "domain/authority";
import { AutopilotDeviceIdentifier, autopilotService } from "services/report/AutopilotService";
import { ReportType } from "services/report/erasure/ReportService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasTenantCookie } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { resolveColor } from "utils/commonFunctions";

import testIds from "testIds.json";

interface Props {
    reportUuid: string;
    onReportDeleted: () => void;
    showForm: (value: boolean) => void;
    editReportUuid: (value: string) => void;
    reportType: ReportType;
    hideEdit?: boolean;
    autopilotDeviceIdentifier?: AutopilotDeviceIdentifier;
    onRecheckComplete: (done: number) => void;
    tableRef: React.RefObject<HTMLElement>;
}

export const ReportTableKebabMenu = (props: Props) => {
    const { t } = useTranslation();
    const [deletionStarted, setDeletionStarted] = React.useState(false);
    const [showAutopilotRecheck, setShowAutopilotRecheck] = React.useState(false);
    const [canRecheck, setCanRecheck] = React.useState(false);

    const showMenuItemEdit =
        !hasTenantCookie() &&
        userSessionService.userHasAllAuthorities([AUTH_REPORT_EDIT]) &&
        props.reportType == "ERASURE" &&
        !props.hideEdit;

    const showMenuItemRecheck =
        props.autopilotDeviceIdentifier != null &&
        userSessionService.userHasAllAuthorities([AUTH_LICENSE_CONSUME]) &&
        !hasTenantCookie();

    const showMenuItemDelete = userSessionService.userHasAllAuthorities([AUTH_REPORT_DELETE]);

    const showMenuItemSeparator = (showMenuItemEdit || showMenuItemRecheck) && showMenuItemDelete;

    const showManageView = () => {
        props.showForm(true);
        props.editReportUuid(props.reportUuid);
    };

    const closeRecheckModal = (licensesConsumed: number) => {
        setShowAutopilotRecheck(false);
        props.onRecheckComplete(licensesConsumed);
    };

    React.useEffect(() => {
        if (props.autopilotDeviceIdentifier) {
            autopilotService.hasValidLicensesForRecheck().then(setCanRecheck);
        }
    }, [props.autopilotDeviceIdentifier]);

    return (
        <>
            <DropdownMenu
                testId={testIds.common.primaryView.table.kebabMenu.itself}
                position={"bottom-start"}
                dropdownOffset={"Xxs"}
                closeOnSelection
                flipOptions={{
                    fallbackPlacements: ["top-start"],
                }}
            >
                <DropdownTrigger>
                    <IconButton>
                        <Kebab color={resolveColor("--theme-icon-fill-default")} />
                    </IconButton>
                </DropdownTrigger>
                <DropdownContent containerRef={props.tableRef}>
                    {showMenuItemEdit && (
                        <DropdownMenuItem
                            onClick={() => {
                                showManageView();
                                usageStatisticsService.sendEvent({
                                    category: Category.REPORTS,
                                    action: Action.EDIT_SINGLE_REPORT,
                                });
                            }}
                            testId={testIds.workArea.report.primaryView.table.kebabMenu.editButton}
                        >
                            {t("Common.edit")}
                        </DropdownMenuItem>
                    )}

                    {showMenuItemRecheck && (
                        <DropdownMenuItem
                            disabled={!canRecheck}
                            onClick={() => {
                                // Logically it's impossible for this to be null but TypeScript doesn't get it.
                                if (props.autopilotDeviceIdentifier != null) {
                                    usageStatisticsService.sendEvent({
                                        category: Category.REPORTS,
                                        action: Action.RECHECK_SINGLE_REPORT,
                                    });
                                    setShowAutopilotRecheck(true);
                                }
                            }}
                            testId={testIds.workArea.report.primaryView.table.kebabMenu.recheckButton}
                        >
                            {t("Common.recheck")}
                        </DropdownMenuItem>
                    )}

                    {showMenuItemSeparator && <DropdownMenuSeparator />}

                    {showMenuItemDelete && (
                        <DropdownMenuItem
                            errorStyle
                            onClick={() => {
                                setDeletionStarted(true);
                                usageStatisticsService.sendEvent({
                                    category: Category.REPORTS,
                                    action: Action.REMOVE_SINGLE_REPORT,
                                });
                            }}
                            testId={testIds.workArea.report.primaryView.table.kebabMenu.deleteButton}
                        >
                            {t("Common.delete")}
                        </DropdownMenuItem>
                    )}
                </DropdownContent>
            </DropdownMenu>

            <ReportRecheck
                devices={props.autopilotDeviceIdentifier ? [props.autopilotDeviceIdentifier] : []}
                showModal={showAutopilotRecheck}
                onClose={closeRecheckModal}
            />

            <SingleReportDeletion
                reportUuid={props.reportUuid}
                deletionInvoked={deletionStarted}
                onDeletionInvoked={setDeletionStarted}
                onReportDeleted={props.onReportDeleted}
            />
        </>
    );
};
